import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i} className="font-semibold text-terracotta">{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
  });

const AssistantReply = ({ content }) => {
  const blocks = content.trim().split(/\n\s*\n/);
  return (
    <div className="space-y-3">
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        const isBulletBlock = lines.every((l) => /^\s*[-•*]\s+/.test(l) || !l.trim());
        if (isBulletBlock) {
          return (
            <ul key={bi} className="space-y-2">
              {lines.filter((l) => l.trim()).map((l, li) => (
                <li key={li} className="flex gap-2.5 items-start">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" />
                  <span className="leading-relaxed">{renderInline(l.replace(/^\s*[-•*]\s+/, ""))}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} className="leading-relaxed">
            {lines.map((l, li) => (
              <React.Fragment key={li}>{li > 0 && <br />}{renderInline(l.replace(/^\s*[-•*]\s+/, "• "))}</React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

const SUGGESTIONS = [
  "What should I expect this week?",
  "How do I manage nausea naturally?",
  "Write me a journal prompt for today.",
  "When is the next vaccination due?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [persona, setPersona] = useState("vansha");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/personas").then((r) => setPersonas(r.data));
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const switchPersona = (id) => {
    if (id === persona) return;
    setPersona(id);
    setSessionId(crypto.randomUUID());
    setMessages([]);
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId, persona });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assistant unavailable.");
      setMessages((m) => [...m, { role: "assistant", content: "I'm having a quiet moment. Please try again shortly." }]);
    } finally { setBusy(false); }
  };

  const currentPersona = personas.find((p) => p.id === persona);

  return (
    <div className="max-w-3xl mx-auto h-[calc(100vh-8rem)] flex flex-col" data-testid="assistant-page">
      <header className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-forest text-saffron flex items-center justify-center">
            <Sparkle weight="thin" size={22} />
          </div>
          <div>
            <h1 className="font-serif text-3xl">{currentPersona?.name || "Vansha"}</h1>
            <p className="text-xs overline">Your family concierge · Claude Sonnet 4.5</p>
          </div>
        </div>
      </header>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="persona-switcher">
        {personas.map((p) => (
          <button key={p.id} onClick={() => switchPersona(p.id)} data-testid={`persona-${p.id}`}
            className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0 ${persona === p.id ? "bg-terracotta text-white" : "border border-ink-20 text-ink-700 hover:bg-vansha-accent"}`}>
            {p.name}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto vansha-card p-6 space-y-6 mb-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-8" data-testid="chat-empty">
            <p className="font-serif text-2xl mb-2">How can I help today?</p>
            <p className="text-ink-500 text-sm mb-6">{currentPersona?.id !== "vansha" ? "Speaking as your " + currentPersona?.name + "." : "A gentle voice for the questions that matter."}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="text-xs px-3 py-2 rounded-full border border-ink-20 text-ink-700 hover:bg-vansha-accent" data-testid={`suggestion-${SUGGESTIONS.indexOf(s)}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${i}`}>
            <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${m.role === "user" ? "bg-terracotta text-white" : "bg-vansha-accent text-ink-900"}`}>
              {m.role === "assistant" ? (
                <div className="font-serif text-lg"><AssistantReply content={m.content} /></div>
              ) : (
                <p className="leading-relaxed whitespace-pre-wrap text-sm">{m.content}</p>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="chat-busy">
            <div className="bg-vansha-accent rounded-2xl px-5 py-3 text-ink-500 italic">{currentPersona?.name || "Vansha"} is thinking…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-3" data-testid="chat-form">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything…" className="input-line flex-1 px-4 rounded-full border border-ink-20" data-testid="chat-input" />
        <button type="submit" disabled={busy || !input.trim()} className="btn-primary" data-testid="chat-send"><PaperPlaneTilt weight="thin" size={18} /></button>
      </form>
    </div>
  );
}
